import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { DiscardChangeModal } from '@/components';
import AppSaveBar from '@/components/AppSaveBar/AppSaveBar';
import Layout from '@/components/layout';
import SkeletonPage from '@/components/SkeletonPage';
import { config } from '@/config';
import { DEFAULT_WEEKLY_WORKING_DATA } from '@/constants';
import { DELIVERY_RESTRICTION_CHOICE, LocationViewChoices, PricingPlan, ProductOverrideType } from '@/constants/enum';
import { PATH } from '@/constants/path';
import {
  handleScrollToElement,
  isAvailableFeature,
  isEmpty,
  openErrorToast,
  openToast,
  stringToArrayByComma,
  time24h,
} from '@/helpers';
import { checkDirtyField } from '@/helpers/compare';
import {
  useCreateDeliveryZoneMutation,
  useGetCrispDataQuery,
  useGetDetailLocationQuery,
  useGetDetailLocationSettingQuery,
  useGetGeneralSettingQuery,
  useGetListCitiesQuery,
  useGetLocationsQuery,
  useGetPostalCodeValidationQuery,
  useUpdateDetailLocationSettingMutation,
} from '@/redux/query';
import {
  currentDeliverySelector,
  deliveryCollectionSelector,
  deliveryHolidaySelector,
  deliveryOverrideDateRangesSelector,
  deliveryOverrideHolidaysSelector,
  deliveryOverrideSpecificWorkingDaysSelector,
  deliveryProductSelector,
  deliverySettingSelector,
  deliverySpecificWorkingDaySelector,
  deliveryWeeklyWorkingDaySelector,
  deliveryZoneErrorSelector,
  handleResetDelivery,
  handleResetDeliveryInitial,
  handleResetDeliveryState,
  handleSaveBackupDeliveryCollections,
  handleSaveBackupDeliveryHolidays,
  handleSaveBackupDeliveryProducts,
  handleSaveBackupDeliverySettings,
  handleSaveBackupDeliverySpecificWorkingDays,
  handleSaveBackupDeliveryWeeklyWorkingDays,
  handleSaveBackupDeliveryWeeklyWorkingDaysLastState,
  handleSaveBackupOverrideDeliveryDateRanges,
  handleSaveBackupOverrideDeliveryHolidays,
  handleSaveBackupOverrideDeliverySpecificWorkingDays,
  handleSaveDeliveryCollections,
  handleSaveDeliveryHolidays,
  handleSaveDeliveryProducts,
  handleSaveDeliverySettings,
  handleSaveDeliverySpecificWorkingDays,
  handleSaveDeliveryWeeklyWorkingDays,
  handleSaveDeliveryWeeklyWorkingDaysLastState,
  handleSaveOverrideDeliveryDateRanges,
  handleSaveOverrideDeliveryHolidays,
  handleSaveOverrideDeliverySpecificWorkingDays,
  handleSetDeliveryDisable,
  handleSetDeliveryState,
  oldDeliverySelector,
} from '@/redux/slice/delivery.slice';
import { errorFieldsSelector, handleResetErrorFields } from '@/redux/slice/error.slice';
import { handleLocationTab } from '@/redux/slice/tab.slice';
import { BlockStack } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import BlackoutDate from './component/BlackoutDates';
import DeliveryAvailability from './component/DeliveryAvailability';
import DeliveryChoice from './component/DeliveryChoice';
import DeliveryLocation from './component/DeliveryLocation/DeliveryLocation';
import DeliveryTags from './component/DeliveryTags';
import DeliveryZone from './component/DeliveryZone';
import DeliveryZoneStatus from './component/DeliveryZoneStatus';
import { ProductAvailability } from './component/ProductAvailability';
import { ProductOverride } from './component/ProductOverride';
import VisualDeliveryDays from './component/VisualDeliveryDays';
import DeliveryMinimum from './component/DeliveryMinimum';
import { generalSettingSelector, handleSaveSettings } from '@/redux/slice/setting.slice';
import DeliveryZoneRadius from './component/DeliveryZoneRadius';

const DeliverySettingsPage = () => {
  const { t } = useTranslation('deliverySettings');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { id: settingId, zoneId } = useParams();
  const { refetch: refetchLocationData } = useGetLocationsQuery({ shop: config.shop });
  const { data: validationPostalCodeData } = useGetPostalCodeValidationQuery({ shop: config.shop });
  const { data: detailLocationData } = useGetDetailLocationQuery({ shop: config.shop, id: Number(zoneId) }, { skip: !zoneId });
  const {
    data: detailDeliverySettingData,
    isLoading: isLoadingDetailSettingData,
    refetch: refetchDeliverySettingData,
  } = useGetDetailLocationSettingQuery({ shop: config.shop, id: Number(settingId) }, { skip: !settingId });
  const [updateDeliveryZoneSetting, { isLoading: isLoadingUpdateDeliveryZoneSetting }] = useUpdateDetailLocationSettingMutation();
  const [createDeliveryZone, { isLoading: isLoadingCreateDeliveryZoneSetting }] = useCreateDeliveryZoneMutation();
  const { data: listCitiesData } = useGetListCitiesQuery(
    {
      perPage: -1,
      countryCode:
        detailDeliverySettingData?.data?.setting.location.address?.countryCode ||
        detailLocationData?.data.address.countryCode ||
        '',
    },
    {
      skip:
        !detailDeliverySettingData?.data?.setting.location.address?.countryCode && !detailLocationData?.data.address.countryCode,
    },
  );
  const { data: generalSettingData, isLoading: isLoadingGeneralSettingData } = useGetGeneralSettingQuery(
    { shop: config.shop },
    { refetchOnMountOrArgChange: true },
  );
  const { data: crispData } = useGetCrispDataQuery({ shop: config.shop });

  const [isOpenDiscardModal, setOpenDiscardModal] = useState<boolean>(false);
  const currentDeliveryData = useSelector(currentDeliverySelector);
  const oldDeliveryData = useSelector(oldDeliverySelector);

  const isChangeDataDelivery = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { deliveryState, deliveryZoneError, ...oldRest } = oldDeliveryData;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      deliveryState: currentDeliveryState,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      deliveryZoneError: currentDeliveryZoneError,
      ...currentRest
    } = currentDeliveryData;
    return checkDirtyField(currentRest, oldRest).isDirty;
  }, [currentDeliveryData, oldDeliveryData]);

  const locationDetail = useMemo(() => {
    const settingLocation = detailDeliverySettingData?.data.setting.location;
    const fallbackLocation = detailLocationData?.data;

    const location = settingId ? settingLocation : fallbackLocation;

    return {
      name: location?.name || '',
      address1: location?.address?.address1 || '',
      city: location?.address?.city || '',
      postalCode: location?.address?.postalCode || '',
    };
  }, [detailDeliverySettingData, detailLocationData, settingId]);

  const convertListCities = useMemo(() => {
    if (listCitiesData?.data.result && listCitiesData?.data.result.length > 0) {
      return (
        listCitiesData?.data.result.map((item) => ({
          label: item.name,
          value: item.name,
        })) || []
      );
    } else {
      const countryLabel = detailDeliverySettingData?.data?.setting.location.address?.country || '';
      if (!countryLabel) return [];
      return [
        {
          label: countryLabel,
          value: countryLabel,
        },
      ];
    }
  }, [detailDeliverySettingData?.data?.setting.location.address?.country, listCitiesData?.data.result]);

  const deliverySettings = useSelector(deliverySettingSelector);
  const deliveryWeeklyWorkingDays = useSelector(deliveryWeeklyWorkingDaySelector);
  const deliverySpecificWorkingDays = useSelector(deliverySpecificWorkingDaySelector);
  const deliveryHolidays = useSelector(deliveryHolidaySelector);
  const errorFields = useSelector(errorFieldsSelector);
  const postalCodeErrors = useSelector(deliveryZoneErrorSelector);
  const [listPostalCode, setListPostalCode] = useState<{ exactCode: string[]; partialCode: string[] }>({
    exactCode: [],
    partialCode: [],
  });
  const deliveryProducts = useSelector(deliveryProductSelector);
  const deliveryCollections = useSelector(deliveryCollectionSelector);
  const deliveryOverrideSpecificWorkingDays = useSelector(deliveryOverrideSpecificWorkingDaysSelector);
  const deliveryOverrideHoliday = useSelector(deliveryOverrideHolidaysSelector);
  const deliveryOverrideDateRange = useSelector(deliveryOverrideDateRangesSelector);
  const generalSettings = useSelector(generalSettingSelector);

  const handleDiscard = () => {
    dispatch(handleResetErrorFields());
    dispatch(handleResetDelivery());
    setOpenDiscardModal(false);
  };

  const handleLeavePage = () => {
    handleDiscard();
    setTimeout(() => {
      dispatch(handleLocationTab({ key: 'locationTab', value: LocationViewChoices.DELIVERY }));
      navigate({ pathname: PATH.LOCATIONS.pathname });
    }, 100);
  };
  const handleStay = () => setOpenDiscardModal(false);

  const handleBackAction = useCallback(() => {
    if (isChangeDataDelivery) {
      setOpenDiscardModal(true);
    } else {
      handleLeavePage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChangeDataDelivery]);

  const onSubmit = async () => {
    dispatch(handleSetDeliveryState({ isSaving: true, isLoading: true, isCheckingTimeSlot: true }));
    if (deliverySettings.isActive) {
      if (
        generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.POSTAL_CODE_MATCH &&
        ((!deliverySettings?.postalCodeValidation?.exactMatch &&
          !deliverySettings.postalCodeValidation.partialMatchPostalCodes.length) ||
          (deliverySettings?.postalCodeValidation?.exactMatch &&
            !deliverySettings.postalCodeValidation.exactMatchPostalCodes.length))
      ) {
        dispatch(
          handleSetDeliveryState({
            isSaving: true,
            isCheckingTimeSlot: true,
            isLoading: false,
          }),
        );
        handleScrollToElement('postal-code');
        return;
      }
      if (
        generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.POSTAL_CODE_MATCH &&
        ((deliverySettings?.postalCodeValidation?.exactMatch && Object.values(postalCodeErrors.exact).some((item) => !!item)) ||
          (!deliverySettings?.postalCodeValidation?.exactMatch && Object.values(postalCodeErrors.partial).some((item) => !!item)))
      ) {
        dispatch(
          handleSetDeliveryState({
            isSaving: true,
            isCheckingTimeSlot: true,
            isLoading: false,
          }),
        );
        handleScrollToElement('postal-code');
        return;
      }
      if (
        generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.CUSTOM &&
        deliverySettings.deliveryRestrictionRange <= 0
      ) {
        dispatch(
          handleSetDeliveryState({
            isSaving: true,
            isCheckingTimeSlot: true,
            isLoading: false,
          }),
        );
        handleScrollToElement('postal-code');
        return;
      }
    }
    if (errorFields?.errorFields && Object.values(errorFields?.errorFields).some((item) => item)) {
      dispatch(handleSetDeliveryState({ isLoading: false }));
      if (errorFields.errorFields?.rule_addTimeSlot) {
        handleScrollToElement('delivery-time-slot');
        return;
      }
      if (errorFields.errorFields?.errorSpecificDays) {
        handleScrollToElement('delivery-specific-workingdays');
      }
      if (errorFields.errorFields?.errorSpecificProduct) {
        handleScrollToElement('delivery-specific-product');
        return;
      }
      if (errorFields.errorFields.emptyDateRange || errorFields.errorFields.emptyProductsAndCollections) {
        handleScrollToElement('delivery-override-rules');
        return;
      }
      return;
    }

    let overrideData;

    if (Number(deliverySettings.overrideType) === ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS) {
      overrideData = deliveryOverrideSpecificWorkingDays.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        productIds: item.productIds,
        collectionIds: item.collectionIds,
        overrideDates: item.overrideDates,
      }));
    } else if (Number(deliverySettings.overrideType) === ProductOverrideType.OVERRIDE_HOLIDAYS) {
      overrideData = deliveryOverrideHoliday.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        productIds: item.productIds,
        collectionIds: item.collectionIds,
        overrideDates: item.overrideDates,
      }));
    } else {
      overrideData = deliveryOverrideDateRange.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        productIds: item.productIds,
        collectionIds: item.collectionIds,
        overridePreparationDay: item.overridePreparationDay,
        overrideDeliveryDay: item.overrideDeliveryDay,
      }));
    }

    const dataSubmit = {
      shop: deliverySettings.shop,
      locationId: deliverySettings.locationId,
      name: isEmpty(deliverySettings.name) ? 'Default zone' : deliverySettings.name,
      city: deliverySettings.city,
      additionalNote: deliverySettings.additionalNote,
      isActive: deliverySettings.isActive,
      isEnable: deliverySettings.isEnable,
      isDefault: deliverySettings.isDefault,
      dateChoice: deliverySettings.deliveryChoice,
      workingDayType: deliverySettings.workingDayType,
      timeSlotType: deliverySettings.deliveryTimeSlotType,
      preparationTimeType: deliverySettings.deliveryPreparationTimeType,
      orderLimitType: deliverySettings.orderLimitType,
      overrideRuleType: Number(deliverySettings.overrideType),
      productAvailabilityType: deliverySettings.productAvailabilityType,
      minimumOrderAmount:
        deliverySettings.minimumOrderAmount === '' ||
          deliverySettings.minimumOrderAmount === null ||
          isNaN(Number(deliverySettings.minimumOrderAmount))
          ? null
          : Number(deliverySettings.minimumOrderAmount),
      dateRange: {
        unit: deliverySettings.deliveryDateRange.unit,
        value: Number(deliverySettings.deliveryDateRange.value),
      },
      isEnablePreparationTime: deliverySettings.isEnablePreparationTime,
      additionalTags: deliverySettings.additionalTags,
      holidays: deliveryHolidays.map((item) => ({
        year: item.year,
        month: item.month,
        dates: item.dates,
      })),
      specificWorkingDays: deliverySpecificWorkingDays.map((item) => ({
        year: item.year,
        month: item.month,
        dates: item.dates,
      })),
      weeklyWorkingDays: deliveryWeeklyWorkingDays.map((item) => ({
        isEnable: item.isEnable,
        isEnableCutOffTime: item.isEnableCutOffTime,
        cutOffTime: item.cutOffTime,
        cutOffPreparationTime: {
          unit: item.cutOffPreparationTime.unit,
          value: Number(item.cutOffPreparationTime.value),
        },
        day: item.day,
        workingHoursStart: item.workingHours.start,
        workingHoursEnd: item.workingHours.end,
        orderLimitPerDay: item.orderLimitPerDay,
        preparationTime: {
          unit: item.preparationTime.unit,
          value: Number(item.preparationTime.value),
        },
        dateRange: {
          unit: item.dateRange.unit,
          value: Number(item.dateRange.value),
        },
        exactTimeSlots: item.exactTimes,
        timeSlots: item.timeSlots,
      })),
      postalCodeValidation: {
        isEnable: deliverySettings.postalCodeValidation.isEnable,
        exactMatch: deliverySettings.postalCodeValidation.exactMatch,
        exactMatchPostalCodes: deliverySettings.postalCodeValidation.exactMatchPostalCodes,
        partialMatchPostalCodes: deliverySettings.postalCodeValidation.partialMatchPostalCodes,
        ukPostcodeValidation: deliverySettings.postalCodeValidation.ukPostcodeValidation,
      },
      productAvailabilityProducts: deliveryProducts,
      productAvailabilityCollections: deliveryCollections,
      overrideRules: overrideData,
      deliveryRestrictionRange: deliverySettings.deliveryRestrictionRange,
    };

    const dataUpdate = {
      ...dataSubmit,
      id: Number(settingId),
    };

    if (settingId) {
      updateDeliveryZoneSetting(dataUpdate)
        .unwrap()
        .then(() => {
          refetchDeliverySettingData();
          refetchLocationData();
          dispatch(handleResetDeliveryState());
          openToast(dispatch);
        })
        .catch(() => {
          openErrorToast(dispatch);
          dispatch(handleResetDeliveryState());
        });
    } else {
      dispatch(handleSetDeliveryState({ isLoading: true }));
      createDeliveryZone(dataSubmit)
        .unwrap()
        .then(() => {
          refetchLocationData().then((response) => {
            dispatch(handleSetDeliveryState({ isLoading: false }));
            dispatch(handleResetDeliveryState());
            openToast(dispatch);
            const zoneId = response?.data?.data[0].settings[response?.data?.data[0].settings.length - 1].id;
            setTimeout(() => {
              navigate({
                pathname: `${PATH.DELIVERY.pathname}/${zoneId}`,
                search: searchParams.toString(),
              });
            }, 100);
          });
        })
        .catch(() => {
          dispatch(handleSetDeliveryState({ isLoading: false }));
          openErrorToast(dispatch);
          dispatch(handleResetDeliveryState());
        });
    }
  };

  useEffect(() => {
    if (!settingId) {
      dispatch(handleResetDeliveryInitial());
      dispatch(
        handleSetDeliveryDisable({
          isDisableSpecificProduct: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
          isDisableProductOverride: !isAvailableFeature(PricingPlan.Smart, generalSettingData?.data.plan as PricingPlan),
          isDisableOrderLimitPerTimeSlot: !isAvailableFeature(
            PricingPlan.Essential,
            generalSettingData?.data.plan as PricingPlan,
          ),
          isDisableMinimumOrderValue: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
        }),
      );
      dispatch(
        handleSaveDeliverySettings({
          shop: config.shop,
          locationId: Number(zoneId),
        }),
      );
      dispatch(
        handleSaveBackupDeliverySettings({
          shop: config.shop,
          locationId: Number(zoneId),
        }),
      );
      setOpenDiscardModal(false);
      dispatch(handleSaveDeliveryWeeklyWorkingDays(DEFAULT_WEEKLY_WORKING_DATA));
      dispatch(handleSaveDeliveryWeeklyWorkingDaysLastState(DEFAULT_WEEKLY_WORKING_DATA));
      dispatch(handleSaveBackupDeliveryWeeklyWorkingDays(DEFAULT_WEEKLY_WORKING_DATA));
      dispatch(handleSaveBackupDeliveryWeeklyWorkingDaysLastState(DEFAULT_WEEKLY_WORKING_DATA));
      if (validationPostalCodeData?.data)
        setListPostalCode({
          exactCode: validationPostalCodeData.data.exactCode,
          partialCode: validationPostalCodeData.data.partialCode,
        });
      return;
    }

    if (!detailDeliverySettingData?.data) {
      console.log('Data is undefined');
      dispatch(handleResetDeliveryInitial());
      return;
    }

    setListPostalCode({
      exactCode: detailDeliverySettingData.data.postalCodes?.exactCode,
      partialCode: detailDeliverySettingData.data.postalCodes?.partialCode,
    });

    const deliverySettingData = {
      id: detailDeliverySettingData.data.setting.id,
      shop: detailDeliverySettingData.data.setting.shop,
      name: detailDeliverySettingData.data.setting.name || t('deliverySettings.locationAddress.defaultZone'),
      city: detailDeliverySettingData.data.setting.city,
      locationId: detailDeliverySettingData.data.setting.locationId,
      isEnable: detailDeliverySettingData.data.setting.isEnable,
      isDefault: detailDeliverySettingData.data.setting.isDefault,
      isActive: detailDeliverySettingData.data.setting.isActive,
      deliveryChoice: detailDeliverySettingData.data.setting.dateChoice,
      workingDayType: detailDeliverySettingData.data.setting.workingDayType,
      deliveryTimeSlotType: detailDeliverySettingData.data.setting.timeSlotType,
      deliveryPreparationTimeType: detailDeliverySettingData.data.setting.preparationTimeType,
      orderLimitType: detailDeliverySettingData.data.setting.orderLimitType,
      overrideType: detailDeliverySettingData.data.setting.overrideRuleType,
      deliveryDateRange: {
        unit: detailDeliverySettingData.data.setting.dateRange.unit,
        value: detailDeliverySettingData.data.setting.dateRange.value.toString(),
      },
      productAvailabilityType: detailDeliverySettingData.data.setting.productAvailabilityType,
      isEnablePreparationTime: detailDeliverySettingData.data.setting.isEnablePreparationTime,
      additionalTags: stringToArrayByComma(detailDeliverySettingData.data.setting.additionalTags),
      postalCodeValidation: {
        id: detailDeliverySettingData.data.setting.postalCodeValidation?.id,
        deliverySettingId: detailDeliverySettingData.data.setting.postalCodeValidation?.settingId,
        isEnable: detailDeliverySettingData.data.setting?.postalCodeValidation?.isEnable,
        exactMatch: detailDeliverySettingData.data.setting.postalCodeValidation?.exactMatch,
        exactMatchPostalCodes: stringToArrayByComma(
          detailDeliverySettingData.data.setting.postalCodeValidation?.exactMatchPostalCodes,
        ),
        partialMatchPostalCodes: stringToArrayByComma(
          detailDeliverySettingData.data.setting.postalCodeValidation?.partialMatchPostalCodes,
        ),
        ukPostcodeValidation: detailDeliverySettingData.data.setting.postalCodeValidation?.ukPostcodeValidation,
      },
      minimumOrderAmount: detailDeliverySettingData.data.setting.minimumOrderAmount?.toString() || null,
      updatedAt: detailDeliverySettingData.data.setting.updatedAt,
      createdAt: detailDeliverySettingData.data.setting.createdAt,
      deliveryRestrictionRange: detailDeliverySettingData.data.setting.deliveryRestrictionRange,
    };

    const deliveryWeeklyWorkingDaysData =
      detailDeliverySettingData.data.setting.weeklyWorkingDays?.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        isEnable: item.isEnable,
        day: item.day,
        workingHours: {
          end: item.workingHoursEnd,
          start: item.workingHoursStart,
        },
        preparationTime: {
          unit: item.preparationTime?.unit,
          value: item.preparationTime?.value,
        },
        isEnableCutOffTime: item.isEnableCutOffTime,
        cutOffTime: item.cutOffTime,
        cutOffPreparationTime: {
          unit: item.cutOffPreparationTime?.unit,
          value: item.cutOffPreparationTime?.value,
        },
        dateRange: {
          unit: item.dateRange?.unit,
          value: item.dateRange?.value,
        },
        exactTimes: item.exactTimeSlots,
        timeSlots: item.timeSlots,
        orderLimitPerDay: item.orderLimitPerDay,
        pickupFrequency: item.pickupFrequency
      })) || [];

    const specificWorkingDaysData =
      detailDeliverySettingData.data.setting.specificWorkingDays?.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        year: item.year,
        month: item.month,
        dates: stringToArrayByComma(item.dates).map((date) => Number(date)),
        updatedAt: item.updatedAt,
        createdAt: item.createdAt,
      })) || [];

    const holidayData =
      detailDeliverySettingData.data.setting.holidays?.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        year: item.year,
        month: item.month,
        dates: stringToArrayByComma(item.dates).map((date) => Number(date)),
        updatedAt: item.updatedAt,
        createdAt: item.createdAt,
      })) || [];

    const deliveryProductData = detailDeliverySettingData.data.setting.productAvailabilityProducts;
    const deliveryCollectionData = detailDeliverySettingData.data.setting.productAvailabilityCollections;

    if (detailDeliverySettingData.data.setting.overrideRules) {
      const deliveryOverrideDateRule =
        detailDeliverySettingData.data.setting.overrideRules?.map((item) => ({
          id: item.id,
          settingId: item.settingId,
          productIds: item.productIds,
          collectionIds: item.collectionIds,
          overrideDates: item.overrideDates,
        })) || [];

      const deliveryOverrideTimeRule =
        detailDeliverySettingData.data.setting.overrideRules?.map((item) => ({
          id: item.id,
          settingId: item.settingId,
          productIds: item.productIds,
          collectionIds: item.collectionIds,
          overridePreparationDay: item.overridePreparationDay,
          overrideDeliveryDay: item.overrideDeliveryDay,
        })) || [];

      if (detailDeliverySettingData.data.setting.overrideRuleType === ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS) {
        dispatch(handleSaveOverrideDeliverySpecificWorkingDays(deliveryOverrideDateRule));
        dispatch(handleSaveBackupOverrideDeliverySpecificWorkingDays(deliveryOverrideDateRule));
      } else if (detailDeliverySettingData.data.setting.overrideRuleType === ProductOverrideType.OVERRIDE_HOLIDAYS) {
        dispatch(handleSaveOverrideDeliveryHolidays(deliveryOverrideDateRule));
        dispatch(handleSaveBackupOverrideDeliveryHolidays(deliveryOverrideDateRule));
      } else {
        dispatch(handleSaveOverrideDeliveryDateRanges(deliveryOverrideTimeRule));
        dispatch(handleSaveBackupOverrideDeliveryDateRanges(deliveryOverrideTimeRule));
      }
    }

    dispatch(handleSaveDeliverySettings(deliverySettingData));
    dispatch(handleSaveDeliveryWeeklyWorkingDays(deliveryWeeklyWorkingDaysData));
    dispatch(handleSaveDeliveryWeeklyWorkingDaysLastState(deliveryWeeklyWorkingDaysData));
    dispatch(handleSaveDeliverySpecificWorkingDays(specificWorkingDaysData));
    dispatch(handleSaveDeliveryHolidays(holidayData));
    dispatch(handleSaveDeliveryProducts(deliveryProductData));
    dispatch(handleSaveDeliveryCollections(deliveryCollectionData));
    dispatch(handleSaveBackupDeliverySettings(deliverySettingData));
    dispatch(handleSaveBackupDeliveryWeeklyWorkingDays(deliveryWeeklyWorkingDaysData));
    dispatch(handleSaveBackupDeliveryWeeklyWorkingDaysLastState(deliveryWeeklyWorkingDaysData));
    dispatch(handleSaveBackupDeliverySpecificWorkingDays(specificWorkingDaysData));
    dispatch(handleSaveBackupDeliveryHolidays(holidayData));
    dispatch(handleSaveBackupDeliveryProducts(deliveryProductData));
    dispatch(handleSaveBackupDeliveryCollections(deliveryCollectionData));
    dispatch(handleResetDeliveryState());
    setOpenDiscardModal(false);
  }, [
    detailDeliverySettingData?.data,
    detailDeliverySettingData?.data.setting,
    dispatch,
    generalSettingData?.data.plan,
    settingId,
    t,
    validationPostalCodeData?.data,
    zoneId,
  ]);

  useEffect(() => {
    dispatch(
      handleSetDeliveryDisable({
        isDisableSpecificProduct: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
        isDisableProductOverride: !isAvailableFeature(PricingPlan.Smart, generalSettingData?.data.plan as PricingPlan),
        isDisableOrderLimitPerTimeSlot: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
        isDisableMinimumOrderValue: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
      }),
    );
    dispatch(handleSaveSettings({ deliveryRestrictionType: generalSettingData?.data.deliveryRestrictionType }));
  }, [dispatch, generalSettingData?.data.deliveryRestrictionType, generalSettingData?.data.plan]);

  const handleRenderDeliveryZone = (deliveryRestrictionChoice: number) => {
    switch (deliveryRestrictionChoice) {
      case DELIVERY_RESTRICTION_CHOICE.NO_VALID:
        return null;
      case DELIVERY_RESTRICTION_CHOICE.POSTAL_CODE_MATCH:
        return <DeliveryZone dataPostalCode={listPostalCode} />;
      case DELIVERY_RESTRICTION_CHOICE.CUSTOM:
        return <DeliveryZoneRadius unit={generalSettingData?.data.restrictionDistanceUnit} />;
    }
  };

  return isLoadingDetailSettingData || isLoadingGeneralSettingData ? (
    <SkeletonPage />
  ) : (
    <Layout
      title={settingId ? detailDeliverySettingData?.data.setting.name || '' : t('page.createZone')}
      backAction={{
        content: 'Back',
        onAction: handleBackAction,
      }}
    >
      <AppSaveBar
        open={isChangeDataDelivery}
        onSave={onSubmit}
        onDiscard={handleDiscard}
        loading={isLoadingUpdateDeliveryZoneSetting || isLoadingCreateDeliveryZoneSetting}
      />
      <BlockStack gap="400">
        <DeliveryZoneStatus />
        <DeliveryLocation detailLocation={locationDetail} locationCities={convertListCities} />
        {handleRenderDeliveryZone(generalSettings.deliveryRestrictionType)}
        <DeliveryChoice />
        <DeliveryMinimum currency={crispData?.data.currency || ''} />
        <DeliveryAvailability time24h={time24h} />
        <VisualDeliveryDays time24h={time24h} />
        <ProductAvailability />
        <ProductOverride />
        <BlackoutDate />
        <DeliveryTags />
      </BlockStack>
      <DiscardChangeModal
        isOpen={isOpenDiscardModal}
        onClose={() => setOpenDiscardModal(false)}
        onStay={handleStay}
        onLeave={handleLeavePage}
      />
    </Layout>
  );
};

export default DeliverySettingsPage;
